import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Rendered width of a block element, so SVG charts can lay out in real pixels instead of
 * stretching a viewBox (which would distort text). Starts from a sensible fallback — which is
 * also what jsdom keeps, since its ResizeObserver stub never fires.
 */
export function useContainerWidth<T extends HTMLElement>(
  fallback = 640,
): { ref: RefObject<T>; width: number } {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = () => {
      const measured = element.clientWidth;
      if (measured > 0) setWidth(measured);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
}
